import { useEffect, useState } from 'react';
import { Plus, Pencil, Trash2, Briefcase, GripVertical } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import { getVacancies, createVacancy, updateVacancy, deleteVacancy } from '@/lib/api';

const blankVacancyDraft = {
  title: '',
  country: '',
  flag: '',
  description: '',
  salary_from: '',
  housing: '',
  duration: '',
  work_type: '',
  is_active: true,
  order: 0
};

const workTypeOptions = ['Склад', 'Завод', 'Ферма', 'Водитель', 'Любая стабильная'];

const flagOptions = [
  { label: '🇬🇧 Великобритания', flag: '🇬🇧', country: 'Великобритания' },
  { label: '🇩🇪 Германия', flag: '🇩🇪', country: 'Германия' },
  { label: '🇵🇱 Польша', flag: '🇵🇱', country: 'Польша' },
  { label: '🇳🇱 Нидерланды', flag: '🇳🇱', country: 'Нидерланды' },
  { label: '🇨🇿 Чехия', flag: '🇨🇿', country: 'Чехия' },
  { label: '🇦🇹 Австрия', flag: '🇦🇹', country: 'Австрия' },
];

export default function AdminVacancies() {
  const [vacancies, setVacancies] = useState([]);
  const [busy, setBusy] = useState(true);
  const [editorOpen, setEditorOpen] = useState(false);
  const [removePromptOpen, setRemovePromptOpen] = useState(false);
  const [activeRow, setActiveRow] = useState(null);
  const [draft, setDraft] = useState(blankVacancyDraft);

  useEffect(() => {
    fetchRows();
  }, []);

  const fetchRows = async () => {
    try {
      const res = await getVacancies();
      setVacancies(res.data.sort((a, b) => a.order - b.order));
    } catch (err) {
      toast.error('Не удалось загрузить вакансии');
    } finally {
      setBusy(false);
    }
  };

  const startCreate = () => {
    setActiveRow(null);
    setDraft({ ...blankVacancyDraft, order: vacancies.length });
    setEditorOpen(true);
  };

  const startEdit = (vacancy) => {
    setActiveRow(vacancy);
    setDraft({
      title: vacancy.title,
      country: vacancy.country,
      flag: vacancy.flag,
      description: vacancy.description,
      salary_from: vacancy.salary_from,
      housing: vacancy.housing,
      duration: vacancy.duration,
      work_type: vacancy.work_type || '',
      is_active: vacancy.is_active,
      order: vacancy.order
    });
    setEditorOpen(true);
  };

  const askDelete = (vacancy) => {
    setActiveRow(vacancy);
    setRemovePromptOpen(true);
  };

  const handleCountrySelect = (option) => {
    setDraft({
      ...draft,
      flag: option.flag,
      country: option.country
    });
  };

  const persistDraft = async () => {
    if (!draft.title || !draft.country) {
      toast.error('Укажите название и страну');
      return;
    }

    try {
      if (activeRow) {
        await updateVacancy(activeRow.id, draft);
        toast.success('Вакансия обновлена');
      } else {
        await createVacancy(draft);
        toast.success('Вакансия создана');
      }
      setEditorOpen(false);
      fetchRows();
    } catch (err) {
      toast.error('Не удалось сохранить');
    }
  };

  const performDelete = async () => {
    if (!activeRow) return;

    try {
      await deleteVacancy(activeRow.id);
      toast.success('Вакансия удалена');
      setRemovePromptOpen(false);
      fetchRows();
    } catch (err) {
      toast.error('Не удалось удалить');
    }
  };

  if (busy) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-vacancies">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Вакансии</h1>
          <p className="text-slate-500">Управление карточками вакансий</p>
        </div>
        <Button onClick={startCreate} data-testid="add-vacancy-button">
          <Plus className="w-4 h-4 mr-2" />
          Добавить
        </Button>
      </div>

      {vacancies.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Briefcase className="w-12 h-12 mx-auto text-slate-300 mb-4" />
            <p className="text-slate-500">Нет вакансий</p>
            <Button variant="outline" className="mt-4" onClick={startCreate}>
              Добавить первую вакансию
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {vacancies.map((vacancy) => (
            <Card key={vacancy.id} data-testid={`vacancy-card-${vacancy.id}`}>
              <CardContent className="py-4">
                <div className="flex items-center gap-4">
                  <div className="text-3xl">{vacancy.flag}</div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold text-slate-900">{vacancy.title}</h3>
                      {vacancy.is_active ? (
                        <span className="px-2 py-0.5 text-xs bg-green-100 text-green-700 rounded-full">Активна</span>
                      ) : (
                        <span className="px-2 py-0.5 text-xs bg-slate-100 text-slate-500 rounded-full">Скрыта</span>
                      )}
                    </div>
                    <p className="text-sm text-slate-500">{vacancy.country} • {vacancy.salary_from}</p>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => startEdit(vacancy)}
                      data-testid={`edit-vacancy-${vacancy.id}`}
                    >
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => askDelete(vacancy)}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      data-testid={`delete-vacancy-${vacancy.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={editorOpen} onOpenChange={setEditorOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{activeRow ? 'Редактировать вакансию' : 'Новая вакансия'}</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Страна *</Label>
              <div className="grid grid-cols-3 gap-2">
                {flagOptions.map((option) => (
                  <button
                    key={option.flag}
                    type="button"
                    onClick={() => handleCountrySelect(option)}
                    className={`p-2 text-sm rounded-lg border transition-all ${
                      draft.flag === option.flag
                        ? 'border-slate-900 bg-slate-50'
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
              {draft.country && !flagOptions.find(o => o.country === draft.country) && (
                <Input
                  value={draft.country}
                  onChange={(e) => setDraft({ ...draft, country: e.target.value })}
                  placeholder="Другая страна"
                />
              )}
            </div>

            <div className="space-y-2">
              <Label>Название вакансии *</Label>
              <Input
                value={draft.title}
                onChange={(e) => setDraft({ ...draft, title: e.target.value })}
                placeholder="Работа на складе"
                data-testid="vacancy-title-input"
              />
            </div>

            <div className="space-y-2">
              <Label>Описание</Label>
              <Textarea
                value={draft.description}
                onChange={(e) => setDraft({ ...draft, description: e.target.value })}
                placeholder="Комплектовщик, упаковщик..."
                data-testid="vacancy-description-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Зарплата</Label>
                <Input
                  value={draft.salary_from}
                  onChange={(e) => setDraft({ ...draft, salary_from: e.target.value })}
                  placeholder="от £12/час"
                  data-testid="vacancy-salary-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Срок</Label>
                <Input
                  value={draft.duration}
                  onChange={(e) => setDraft({ ...draft, duration: e.target.value })}
                  placeholder="6-12 месяцев"
                  data-testid="vacancy-duration-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Тип работы (для фильтра в квизе)</Label>
              <select
                value={draft.work_type}
                onChange={(e) => setDraft({ ...draft, work_type: e.target.value })}
                className="w-full h-10 px-3 rounded-md border border-slate-200 bg-white text-sm"
                data-testid="vacancy-work-type-select"
              >
                <option value="">— Не указано —</option>
                {workTypeOptions.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
              <p className="text-xs text-slate-500">
                Когда посетитель выберет этот тип работы в квизе — увидит именно эти вакансии.
              </p>
            </div>

            <div className="space-y-2">
              <Label>Жильё</Label>
              <Input
                value={draft.housing}
                onChange={(e) => setDraft({ ...draft, housing: e.target.value })}
                placeholder="Бесплатное общежитие"
                data-testid="vacancy-housing-input"
              />
            </div>

            <div className="flex items-center justify-between">
              <Label>Активна</Label>
              <Switch
                checked={draft.is_active}
                onCheckedChange={(checked) => setDraft({ ...draft, is_active: checked })}
                data-testid="vacancy-active-switch"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditorOpen(false)}>
              Отмена
            </Button>
            <Button onClick={persistDraft} data-testid="save-vacancy-button">
              Сохранить
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <AlertDialog open={removePromptOpen} onOpenChange={setRemovePromptOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Удалить вакансию?</AlertDialogTitle>
            <AlertDialogDescription>
              Вы уверены, что хотите удалить "{activeRow?.title}"? Это действие нельзя отменить.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Отмена</AlertDialogCancel>
            <AlertDialogAction
              onClick={performDelete}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete-vacancy"
            >
              Удалить
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
